import { useState, useEffect, useCallback, useRef } from 'react';
import Badge          from '../components/ui/Badge';
import Icon           from '../components/ui/Icon';
import PullToRefresh  from '../components/ui/PullToRefresh';
import { getServiceRequestsApi } from '../api/serviceRequest';
import { useApproveGuestRequest } from '../hooks/useServiceRequest';

/* ── Normalizers ──────────────────────────────────────────────────────────── */
const SERVICE_META = {
  plumbing:    { icon: '🔧', label: 'Plumbing'    },
  electricity: { icon: '⚡', label: 'Electrician' },
  cleaning:    { icon: '🧹', label: 'Cleaning'    },
};

const STATUS_NORMALIZE = {
  pending:     'Pending',
  in_progress: 'In Progress',
  completed:   'Completed',
  rejected:    'Rejected',
};

const formatScheduledAt = (iso) =>
  new Date(iso).toLocaleString('en-US', { month: 'long', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit' });

const normalize = (r) => {
  const meta   = SERVICE_META[r.service_type] ?? { icon: '🔧', label: r.service_type ?? '—' };
  const status = STATUS_NORMALIZE[r.status] ?? 'Pending';
  const date   = r.created_at
    ? new Date(r.created_at).toLocaleDateString('en-IN', { day: 'numeric', month: 'long', year: 'numeric' })
    : null;
  return {
    id:             r.id,
    title:          r.issue ?? meta.label,
    category:       meta.label,
    icon:           meta.icon,
    status,
    property:       r.property_id ?? null,
    date,
    isGuestRequest: r.is_approved === 0,
    scheduleType:   r.schedule_type ?? 'now',
    scheduledAt:    r.scheduled_at ?? null,
  };
};

/* ── Status config ────────────────────────────────────────────────────────── */
const STATUS_CONFIG = {
  'Pending':     { color: 'orange', label: 'Pending'     },
  'In Progress': { color: 'blue',   label: 'In Progress' },
  'Completed':   { color: 'green',  label: 'Completed'   },
  'Rejected':    { color: 'red',    label: 'Rejected'    },
};

/* ── Filter config ────────────────────────────────────────────────────────── */
const STATUS_FILTERS = [
  { key: 'all',         label: 'All'         },
  { key: 'pending',     label: 'Pending'     },
  { key: 'in_progress', label: 'In Progress' },
  { key: 'completed',   label: 'Completed'   },
  { key: 'rejected',    label: 'Rejected'    },
];

const SCHEDULE_FILTERS = [
  { key: 'all',   label: 'All'       },
  { key: 'now',   label: 'Now'       },
  { key: 'later', label: 'Scheduled' },
];

/* ── Request Card (active requests) ──────────────────────────────────────── */
const RequestCard = ({ req, delay, onApproved }) => {
  const cfg = STATUS_CONFIG[req.status] ?? { color: 'gray', label: 'Pending' };
  const { mutate: approve, isPending: isApproving } = useApproveGuestRequest();

  return (
    <div
      className="bg-white rounded-2xl card-shadow animate-fadeUp border border-surface-500 overflow-hidden"
      style={{ animationDelay: delay, animationFillMode: 'forwards', opacity: 0 }}
    >
      {/* Top strip — icon + title + badge */}
      <div className="flex items-center gap-3 px-4 pt-4 pb-3">
        <div
          className="w-11 h-11 rounded-xl flex items-center justify-center text-xl flex-shrink-0"
          style={{ background: '#f1f5f9', border: '1px solid #e2e8f0' }}
        >
          {req.icon}
        </div>
        <div className="flex-1 min-w-0">
          <p className="font-syne font-bold text-slate-800 text-base leading-tight truncate">
            {req.title}
          </p>
          <p className="font-dm text-slate-500 text-xs mt-0.5">{req.category}</p>
        </div>
        <Badge label={cfg.label} color={cfg.color} />
      </div>

      {/* Divider */}
      <div className="mx-4 border-t border-slate-100" />

      {/* Meta row — ID + date */}
      <div className="flex items-center justify-between gap-2 px-4 py-2.5">
        <div className="flex items-center gap-1.5">
          <Icon name="ticket" size={11} className="text-slate-400 flex-shrink-0" />
          <span className="font-dm text-xs text-slate-500 font-medium">{req.id}</span>
        </div>
        {req.date && (
          <div className="flex items-center gap-1.5">
            <Icon name="clock" size={11} className="text-slate-400 flex-shrink-0" />
            <span className="font-dm text-xs text-slate-500">{req.date}</span>
          </div>
        )}
      </div>

      {/* Schedule info */}
      {req.scheduleType === 'later' && req.scheduledAt ? (
        <div className="mx-3 mb-3 flex items-center gap-2.5 px-3 py-2.5 rounded-xl bg-indigo-50 border border-indigo-200">
          <div className="w-7 h-7 rounded-lg bg-indigo-100 flex items-center justify-center flex-shrink-0">
            <Icon name="clock" size={13} className="text-indigo-500" />
          </div>
          <div className="min-w-0">
            <p className="font-dm text-[10px] font-semibold text-indigo-400 uppercase tracking-wide leading-none mb-0.5">Scheduled For</p>
            <p className="font-dm text-xs font-semibold text-indigo-700 leading-tight">{formatScheduledAt(req.scheduledAt)}</p>
          </div>
        </div>
      ) : (
        <div className="mx-3 mb-3 flex items-center gap-2 px-3 py-2 rounded-xl bg-slate-50 border border-slate-200">
          <Icon name="arrow" size={12} className="text-slate-400 flex-shrink-0" />
          <p className="font-dm text-xs font-medium text-slate-500">Immediate request</p>
        </div>
      )}

      {/* Action / status banner */}
      {req.isGuestRequest && req.status === 'Pending' && (
        <div className="mx-3 mb-3 flex items-center justify-between gap-2 px-3 py-2.5 rounded-xl bg-violet-50 border border-violet-200">
          <div className="flex items-center gap-2 min-w-0">
            <Icon name="user" size={13} className="text-violet-500 flex-shrink-0" />
            <p className="font-dm text-xs font-medium text-violet-700 leading-snug">
              Guest request — needs your approval
            </p>
          </div>
          <button
            onClick={() => approve(req.id, { onSuccess: onApproved })}
            disabled={isApproving}
            className="flex-shrink-0 px-3 py-1.5 rounded-lg bg-violet-600 text-white font-dm font-semibold text-xs disabled:opacity-50 transition-opacity press-scale"
          >
            {isApproving ? 'Approving…' : 'Approve'}
          </button>
        </div>
      )}

      {!req.isGuestRequest && req.status === 'Pending' && (
        <div className="mx-3 mb-3 flex items-center gap-2 px-3 py-2.5 rounded-xl bg-amber-50 border border-amber-200">
          <Icon name="clock" size={13} className="text-amber-500 flex-shrink-0" />
          <p className="font-dm text-xs font-medium text-amber-700">
            Awaiting admin review &amp; team assignment
          </p>
        </div>
      )}

      {req.status === 'In Progress' && (
        <div className="mx-3 mb-3 flex items-center gap-2 px-3 py-2.5 rounded-xl bg-sky-50 border border-sky-200">
          <Icon name="arrow" size={13} className="text-sky-500 flex-shrink-0" />
          <p className="font-dm text-xs font-medium text-sky-700">
            A team is currently working on this
          </p>
        </div>
      )}
    </div>
  );
};

/* ── Done Card (completed + rejected) ────────────────────────────────────── */
const DoneCard = ({ req, delay }) => {
  const rejected = req.status === 'Rejected';
  const accentBg     = rejected ? 'rgba(239,68,68,0.06)'  : 'rgba(16,185,129,0.06)';
  const accentBorder = rejected ? 'rgba(239,68,68,0.15)'  : 'rgba(16,185,129,0.15)';

  return (
    <div
      className="bg-white rounded-2xl card-shadow animate-fadeUp border border-surface-500 overflow-hidden"
      style={{ animationDelay: delay, animationFillMode: 'forwards', opacity: 0 }}
    >
      <div className="flex items-center gap-3 px-4 pt-4 pb-3">
        <div
          className="w-11 h-11 rounded-xl flex items-center justify-center text-xl flex-shrink-0"
          style={{ background: accentBg, border: `1px solid ${accentBorder}` }}
        >
          {req.icon}
        </div>
        <div className="flex-1 min-w-0">
          <p className="font-syne font-bold text-slate-800 text-base leading-tight truncate">
            {req.title}
          </p>
          <p className="font-dm text-slate-500 text-xs mt-0.5">{req.category}</p>
        </div>
        <Badge
          label={rejected ? 'Rejected' : 'Completed'}
          color={rejected ? 'red' : 'green'}
        />
      </div>

      <div className="mx-4 border-t border-slate-100" />

      <div className="flex items-center justify-between gap-2 px-4 py-2.5">
        <div className="flex items-center gap-1.5">
          <Icon name="ticket" size={11} className="text-slate-400 flex-shrink-0" />
          <span className="font-dm text-xs text-slate-500 font-medium">{req.id}</span>
        </div>
        {req.date && (
          <div className="flex items-center gap-1.5">
            <Icon name="clock" size={11} className="text-slate-400 flex-shrink-0" />
            <span className="font-dm text-xs text-slate-500">{req.date}</span>
          </div>
        )}
      </div>

      <div className="mx-3 mb-3 flex items-center gap-2.5 px-3 py-2.5 rounded-xl bg-slate-50 border border-slate-200">
        <div className="w-6 h-6 rounded-md bg-slate-100 flex items-center justify-center flex-shrink-0">
          <Icon name="clock" size={12} className="text-slate-400" />
        </div>
        <div className="min-w-0">
          <p className="font-dm text-[10px] font-semibold text-slate-400 uppercase tracking-wide leading-none mb-0.5">
            {req.scheduleType === 'later' ? 'Scheduled For' : 'Schedule'}
          </p>
          <p className="font-dm text-xs font-medium text-slate-600">
            {req.scheduleType === 'later' && req.scheduledAt
              ? formatScheduledAt(req.scheduledAt)
              : 'Immediate request'}
          </p>
        </div>
      </div>
    </div>
  );
};

/* ── Empty state ──────────────────────────────────────────────────────────── */
const EmptyState = ({ label }) => (
  <div className="flex flex-col items-center justify-center py-16 text-center animate-fadeUp" style={{ animationFillMode: 'forwards' }}>
    <div className="w-16 h-16 rounded-2xl bg-surface-700 border border-surface-500 flex items-center justify-center mb-4">
      <Icon name="list" size={24} className="text-surface-300" />
    </div>
    <p className="font-dm text-sm text-surface-200">No {label.toLowerCase()} requests</p>
  </div>
);

/* ── Main Page ────────────────────────────────────────────────────────────── */
const RequestsPage = () => {
  const [items,          setItems]          = useState([]);
  const [page,           setPage]           = useState(1);
  const [total,          setTotal]          = useState(0);
  const [loading,        setLoading]        = useState(false);
  const [hasMore,        setHasMore]        = useState(true);
  const [fetchError,     setFetchError]     = useState(null);
  const [statusFilter,   setStatusFilter]   = useState('all');
  const [scheduleFilter, setScheduleFilter] = useState('all');

  const fetchPage = useCallback(async (pageNum, replace = false) => {
    setLoading(true);
    setFetchError(null);
    try {
      const params = { page: pageNum, limit: 20 };
      if (statusFilter   !== 'all') params.status        = statusFilter;
      if (scheduleFilter !== 'all') params.schedule_type = scheduleFilter;

      const res = await getServiceRequestsApi(params);
      const { data, total: t, totalPages } = res;
      setItems(prev => replace ? data.map(normalize) : [...prev, ...data.map(normalize)]);
      setTotal(t);
      setHasMore(pageNum < totalPages);
      setPage(pageNum);
    } catch (e) {
      setFetchError(e);
    } finally {
      setLoading(false);
    }
  }, [statusFilter, scheduleFilter]);

  useEffect(() => { fetchPage(1, true); }, [fetchPage]);

  // Keep mutable refs so the IntersectionObserver closure is always current
  const hasMoreRef = useRef(hasMore);
  const loadingRef = useRef(loading);
  const pageRef    = useRef(page);
  useEffect(() => { hasMoreRef.current = hasMore; }, [hasMore]);
  useEffect(() => { loadingRef.current = loading; }, [loading]);
  useEffect(() => { pageRef.current    = page;    }, [page]);

  const sentinelRef = useRef(null);
  useEffect(() => {
    const el = sentinelRef.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && hasMoreRef.current && !loadingRef.current) {
          fetchPage(pageRef.current + 1);
        }
      },
      { rootMargin: '200px' }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [fetchPage]); // recreated only when filters change

  const activeStatusLabel = STATUS_FILTERS.find(f => f.key === statusFilter)?.label ?? 'All';

  const renderCard = (r, i) => {
    const isDone = r.status === 'Completed' || r.status === 'Rejected';
    return isDone
      ? <DoneCard    key={r.id} req={r} delay={`${0.07 * i}s`} />
      : <RequestCard key={r.id} req={r} delay={`${0.07 * i}s`} onApproved={() => fetchPage(1, true)} />;
  };

  return (
    <PullToRefresh onRefresh={() => fetchPage(1, true)}>
      <div className="pb-6">

        {/* Status filter chips */}
        <div className="pt-4 pb-2 animate-fadeUp" style={{ animationFillMode: 'forwards' }}>
          <div className="flex gap-2 px-5 overflow-x-auto pb-1" style={{ scrollbarWidth: 'none' }}>
            {STATUS_FILTERS.map((f) => {
              const isActive = statusFilter === f.key;
              return (
                <button
                  key={f.key}
                  onClick={() => setStatusFilter(f.key)}
                  className="flex-shrink-0 px-3.5 py-2 rounded-full font-dm font-semibold text-xs transition-all duration-200"
                  style={
                    isActive
                      ? { background: 'linear-gradient(135deg, #4338ca, #6366f1)', color: '#fff', border: '1px solid transparent' }
                      : { background: 'var(--surface-800, #f8fafc)', color: 'var(--surface-200, #94a3b8)', border: '1px solid var(--surface-500, #e2e8f0)' }
                  }
                >
                  {f.label}
                </button>
              );
            })}
          </div>
        </div>

        {/* Schedule toggle + total badge */}
        <div className="flex items-center justify-between px-5 pb-3 gap-3 animate-fadeUp" style={{ animationFillMode: 'forwards' }}>
          <div className="flex gap-1.5">
            {SCHEDULE_FILTERS.map((f) => {
              const isActive = scheduleFilter === f.key;
              return (
                <button
                  key={f.key}
                  onClick={() => setScheduleFilter(f.key)}
                  className="px-3 py-1.5 rounded-full font-dm font-semibold text-xs transition-all duration-200"
                  style={
                    isActive
                      ? { background: '#e0e7ff', color: '#4338ca', border: '1px solid #c7d2fe' }
                      : { background: 'transparent', color: 'var(--surface-200, #94a3b8)', border: '1px solid var(--surface-500, #e2e8f0)' }
                  }
                >
                  {f.label}
                </button>
              );
            })}
          </div>
          {total > 0 && (
            <span className="font-dm text-xs text-surface-200 flex-shrink-0">
              {total} {activeStatusLabel !== 'All' ? activeStatusLabel.toLowerCase() + ' ' : ''}request{total !== 1 ? 's' : ''}
            </span>
          )}
        </div>

        {/* Loading skeleton */}
        {loading && items.length === 0 && (
          <div className="px-5 flex flex-col gap-4">
            {[1, 2, 3].map((n) => (
              <div key={n} className="bg-surface-800 rounded-2xl p-4 border border-surface-500 card-shadow">
                <div className="flex items-start justify-between mb-3">
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 rounded-2xl shimmer-bg flex-shrink-0" />
                    <div>
                      <div className="h-3.5 w-28 rounded-md shimmer-bg mb-1.5" />
                      <div className="h-2.5 w-16 rounded-md shimmer-bg mb-1" />
                      <div className="h-2 w-20 rounded-md shimmer-bg" />
                    </div>
                  </div>
                  <div className="w-16 h-5 rounded-full shimmer-bg" />
                </div>
                <div className="h-9 rounded-xl shimmer-bg" />
              </div>
            ))}
          </div>
        )}

        {/* Error */}
        {fetchError && (
          <div className="px-5 flex flex-col items-center justify-center py-16 text-center">
            <div className="w-14 h-14 rounded-2xl bg-rose-50 border border-rose-200 flex items-center justify-center mb-3">
              <Icon name="help" size={22} className="text-rose-400" />
            </div>
            <p className="font-dm text-sm text-surface-200">Failed to load requests.</p>
            <button onClick={() => fetchPage(1, true)} className="font-dm text-xs text-brand-600 font-semibold mt-2">
              Try again
            </button>
          </div>
        )}

        {/* Empty state */}
        {!loading && !fetchError && items.length === 0 && (
          <EmptyState label={activeStatusLabel} />
        )}

        {/* Content */}
        {!fetchError && items.length > 0 && (
          <div className="px-5 flex flex-col gap-4">
            {items.map(renderCard)}
          </div>
        )}

        {/* Scroll sentinel — triggers next page load when it enters the viewport */}
        <div ref={sentinelRef} className="h-1" />

        {/* Inline spinner while fetching additional pages */}
        {loading && items.length > 0 && (
          <div className="flex justify-center py-4">
            <span className="w-5 h-5 border-2 border-brand-400 border-t-transparent rounded-full animate-spin" />
          </div>
        )}

      </div>
    </PullToRefresh>
  );
};

export default RequestsPage;
